// frontend/src/screens/ProfileScreen.js
import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../context/AuthContext';
import { authenticatedRequest } from '../services/authApi';
import { API_ORIGIN as API_BASE } from '../services/apiOrigin';
import { getAuthToken } from '../utils/auth';

// --- Enhancements: validation, retry on 401, and utils ---
const MAX_PHOTO_MB = 3;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const MAX_DNI_MB = 5;
const ACCEPTED_DNI_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

// Wrap a fetch and if 401, try refresh once and retry
async function fetchWith401Retry(input, init) {
  return authenticatedRequest(input, { ...init, rawResponse: true, diagSource: 'ProfileScreen' });
}

function validatePhotoFile(file) {
  if (!file) return { ok: true };
  if (!ACCEPTED_TYPES.includes(file.type)) {
    return { ok: false, messageKey: 'editor.validation.invalidPhotoFormat' };
  }
  const mb = file.size / (1024 * 1024);
  if (mb > MAX_PHOTO_MB) {
    return {
      ok: false,
      messageKey: 'editor.validation.photoTooLarge',
      messageOptions: { size: mb.toFixed(1), max: MAX_PHOTO_MB },
    };
  }
  return { ok: true };
}

function validateDniFile(file) {
  if (!file) return { ok: true };
  if (!ACCEPTED_DNI_TYPES.includes(file.type)) {
    return { ok: false, messageKey: 'editor.validation.dniImage' };
  }
  const mb = file.size / (1024 * 1024);
  if (mb > MAX_DNI_MB) {
    return {
      ok: false,
      messageKey: 'editor.validation.dniTooLarge',
      messageOptions: { max: MAX_DNI_MB },
    };
  }
  return { ok: true };
}

const BIO_MAX = 35;



function appendIfFits(text, addition) {
  const base = String(text || '');
  const extra = String(addition || '');
  const next = base + extra;
  return Array.from(next).length <= BIO_MAX ? next : base;
}

// Normaliza a la URL absoluta servida por el backend
function toAbsolutePhotoURL(p) {
  if (!p) return '';
  if (/^https?:\/\//i.test(p)) return p;
  let clean = String(p).replace(/^\/?uploads\//, '').replace(/^\/+/, '');
  if (!clean.startsWith('profile_photos/')) clean = `profile_photos/${clean}`;
  return `${API_BASE}/${clean.split('/').map(encodeURIComponent).join('/')}`;
}

function getStoredTokenSafe() {
  try {
    return getAuthToken();
  } catch {
    return '';
  }
}

// Emoji del género
function genderEmoji(g) {
  if (!g) return '';
  const key = String(g).toLowerCase();
  if (key === 'hombre') return '♂';
  if (key === 'mujer') return '♀';
  return '⚧';
}

// --- Zodiac sign emoji helpers (match user.zodiacSign) ---
const SIGN_EMOJI = {
  aries: '♈',
  tauro: '♉',
  geminis: '♊',
  cáncer: '♋',
  leo: '♌',
  virgo: '♍',
  libra: '♎',
  escorpio: '♏',
  sagitario: '♐',
  capricornio: '♑',
  acuario: '♒',
  piscis: '♓',
};

function normalizeSignKey(str) {
  if (!str) return '';
  return String(str)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function getSignEmoji(sign) {
  if (!sign) return '';
  const key = normalizeSignKey(sign);
  return SIGN_EMOJI[key] || '';
}

const SIGN_TRANSLATION_KEYS = {
  aries: 'aries',
  tauro: 'taurus',
  taurus: 'taurus',
  geminis: 'gemini',
  gemini: 'gemini',
  cancer: 'cancer',
  leo: 'leo',
  virgo: 'virgo',
  libra: 'libra',
  escorpio: 'scorpio',
  scorpio: 'scorpio',
  sagitario: 'sagittarius',
  sagittarius: 'sagittarius',
  capricornio: 'capricorn',
  capricorn: 'capricorn',
  acuario: 'aquarius',
  aquarius: 'aquarius',
  piscis: 'pisces',
  pisces: 'pisces',
};

function canAccessAdminFeedReports(user) {
  if (!user || typeof user !== 'object') return false;

  const role = String(user.role || user.userRole || '')
    .trim()
    .toLowerCase();

  return (
    user.isAdmin === true ||
    role === 'admin' ||
    role === 'gm' ||
    role === 'owner' ||
    role === 'superadmin'
  );
}

const GENDER_OPTIONS = [
  { value: '', labelKey: 'editor.gender.select' },
  { value: 'Hombre', labelKey: 'editor.gender.male' },
  { value: 'Mujer', labelKey: 'editor.gender.female' },
  { value: 'Otro', labelKey: 'editor.gender.other' },
];

const ProfileScreen = () => {
  const { t } = useTranslation('profile');
  const { user, refreshProfile, deleteAccount } = useContext(AuthContext);
  const navigate = useNavigate();

  const [fullName, setFullName] = useState('');
  const [bio, setBio] = useState('');
  const [photo, setPhoto] = useState(null);
  const [dni, setDni] = useState(null);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [gender, setGender] = useState('');
  const [genderChangeLeft, setGenderChangeLeft] = useState(1);

// Local-only UI helpers (no backend impact)
const [showPreview, setShowPreview] = useState(true);

// Emoji picker (carga dinámica para no inflar el bundle)
const [EmojiPicker, setEmojiPicker] = useState(null);
const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiTriggerRef = useRef(null);
  const emojiWrapRef = useRef(null);

  useEffect(() => {
    function handleOutside(e) {
      if (!showEmojiPicker) return;
      const t = e.target;
      if (emojiWrapRef.current && emojiWrapRef.current.contains(t)) return;
      if (emojiTriggerRef.current && emojiTriggerRef.current.contains(t)) return;
      setShowEmojiPicker(false);
    }
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [showEmojiPicker]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const mod = await import('emoji-picker-react');
const Component = mod?.default ?? mod?.EmojiPicker ?? null;
if (!cancelled && Component) setEmojiPicker(() => Component);
      } catch (e) {
        // dependencia no instalada: el trigger queda disabled
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Track initial values to compute "dirty" state
  const [initialBio, setInitialBio] = useState('');
  const [initialGender, setInitialGender] = useState('');

  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const visibleMessage = message && typeof message === 'object'
    ? t(message.key, message.options)
    : message;
  const zodiacTranslationKey = SIGN_TRANSLATION_KEYS[normalizeSignKey(user?.zodiacSign)];
  const zodiacDisplayName = zodiacTranslationKey
    ? t(`home:zodiacSigns.${zodiacTranslationKey}`)
    : String(user?.zodiacSign || '');

  const handleDeleteAccount = async () => {
    if (deleteBusy || deleteConfirmation !== t('editor.deleteAccount.confirmationPhrase')) return;
    setDeleteBusy(true);
    setDeleteError('');
    try {
      await deleteAccount();
      navigate('/', { replace: true });
    } catch {
      setDeleteError(t('editor.deleteAccount.failure'));
      setDeleteBusy(false);
    }
  };

  // Keep last created object URL to revoke and avoid leaks
  const [blobUrl, setBlobUrl] = useState(null);
  const canSeeAdminPanel = canAccessAdminFeedReports(user);
  const [reportCounts, setReportCounts] = useState({ total: 0, pending: 0, reviewed: 0, ignored: 0 });
  const [hasFreshPendingPulse, setHasFreshPendingPulse] = useState(false);

  useEffect(() => {
    if (!user) return;

    const fn = user.fullName || '';
    const b = user.bio || '';
    const g = user.gender || '';
    const pp = user.photoPath ? toAbsolutePhotoURL(user.photoPath) : null;

    setFullName((prev) => (prev === '' ? fn : prev));
    setGenderChangeLeft(typeof user.genderChangeLeft === 'number' ? user.genderChangeLeft : 1);
    setPreviewPhoto((prev) => prev || pp);

    // Solo hidratar campos editables si todavía están alineados con el baseline previo.
    if ((bio || '') === (initialBio || '')) {
      setBio(b);
      setInitialBio(b);
    }
    if ((gender || '') === (initialGender || '')) {
      setGender(g);
      setInitialGender(g);
    }
  }, [user, bio, gender, initialBio, initialGender]);

  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  useEffect(() => {
    if (!canSeeAdminPanel) return undefined;

    let mounted = true;
    let intervalId = null;

    const loadReportCounts = async (isInitial = false) => {
      try {
        const token = getStoredTokenSafe();
        if (!token) return;

        const res = await fetchWith401Retry(`${API_BASE}/api/admin/feed/reports/counts`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          credentials: 'include',
          cache: 'no-store',
        });

        if (res.status === 403) {
  if (intervalId) {
    window.clearInterval(intervalId);
    intervalId = null;
  }
  return;
}

if (!res.ok) return;

        const payload = await res.json().catch(() => null);
        if (!mounted || !payload || typeof payload !== 'object') return;

        setReportCounts((prev) => {
          const next = {
            total: Number(payload.total || 0),
            pending: Number(payload.pending || 0),
            reviewed: Number(payload.reviewed || 0),
            ignored: Number(payload.ignored || 0),
          };

          if (!isInitial && next.pending > prev.pending) {
            setHasFreshPendingPulse(true);
          }

          return next;
        });
      } catch {
        // silent fail: this badge is only a convenience helper for the admin user
      }
    };

    loadReportCounts(true);
    intervalId = window.setInterval(() => loadReportCounts(false), 30000);

    return () => {
      mounted = false;
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [canSeeAdminPanel]);

  useEffect(() => {
    if (!hasFreshPendingPulse) return undefined;
    const timeoutId = window.setTimeout(() => setHasFreshPendingPulse(false), 3200);
    return () => window.clearTimeout(timeoutId);
  }, [hasFreshPendingPulse]);

  const handlePhotoChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const v = validatePhotoFile(file);
    if (!v.ok) {
      setMessage({ key: v.messageKey, options: v.messageOptions });
      // reset input visually
      e.target.value = '';
      return;
    }
    // Revoke previous preview to avoid memory leaks
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
    }
    const url = URL.createObjectURL(file);
    setBlobUrl(url);
    setPhoto(file);
    setPreviewPhoto(url);
  };

  const handleDniChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      setDni(null);
      return;
    }
    const v = validateDniFile(file);
    if (!v.ok) {
      setMessage({ key: v.messageKey, options: v.messageOptions });
      e.target.value = '';
      return;
    }
    setMessage('');
    setDni(file);
  };

  const isDirty = (
    (bio || '') !== (initialBio || '') ||
    (gender || '') !== (initialGender || '') ||
    !!photo ||
    !!dni
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    if (!isDirty) {
      setMessage({ key: 'editor.feedback.noChanges' });
      return;
    }

    if (bio.length > BIO_MAX) {
      setMessage({ key: 'editor.validation.bioTooLong', options: { max: BIO_MAX } });
      return;
    }

    setLoading(true);
    try {
      const token = getStoredTokenSafe();
      if (!token) {
        setMessage({ key: 'editor.feedback.notAuthenticated' });
        navigate('/login');
        return;
        }

      const formData = new FormData();
      formData.append('bio', (bio || '').replace(/\s+/g, ' ').trim());
      if (gender !== '') formData.append('gender', gender);
      if (photo) formData.append('photo', photo, photo.name || 'photo.jpg');
      if (dni) formData.append('dni', dni, dni.name || 'dni.jpg');

      const init = {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      };

      const res = await fetchWith401Retry(`${API_BASE}/api/auth/profile`, init);
      const payload = await res.json().catch(() => ({}));

      if (res.ok) {
  // Actualizamos baseline aunque refreshProfile falle
  setInitialBio(bio);
  setInitialGender(gender);
  setPhoto(null);
  setDni(null);
  setMessage({ key: 'editor.feedback.updated' });
  try { await refreshProfile(); } catch {}
} else if (res.status === 401) {
        setMessage({ key: 'editor.feedback.sessionExpired' });
        navigate('/login');
      } else {
        setMessage(payload.message || { key: 'editor.feedback.updateFailed' });
      }
    } catch (err) {
      setMessage({ key: 'editor.feedback.connectionError' });
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    const canGoBack = typeof window !== 'undefined' && window.history?.state && window.history.state.idx > 0;
    if (canGoBack) navigate(-1);
    else navigate('/home');
  };

  return (
    <div
      id="profileBgHost"
      style={{
        ...styles.container,
        position: 'relative',
        isolation: 'isolate'
      }}
    >
      <style>{`
  #profileBgHost {
    background:
      radial-gradient(circle at 50% -12%, rgba(218,165,32,0.18), transparent 28%),
      radial-gradient(circle at 12% 16%, rgba(183,44,255,0.28), transparent 36%),
      radial-gradient(circle at 86% 70%, rgba(138,43,226,0.24), transparent 38%),
      linear-gradient(180deg, #03000b 0%, #100020 48%, #05000d 100%);
    min-height: 100vh;
    min-height: 100svh;
    min-height: 100dvh;
    overflow-x: hidden;
    overflow-y: visible;
    touch-action: pan-y;
  }

  #profileBgHost::before {
    content: "";
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: -2;
    background-image:
      radial-gradient(circle at 10% 8%, rgba(255,255,255,.78) 0 1px, transparent 1.8px),
      radial-gradient(circle at 78% 7%, rgba(255,215,0,.64) 0 1px, transparent 1.8px),
      radial-gradient(circle at 94% 20%, rgba(183,44,255,.62) 0 1px, transparent 1.8px),
      radial-gradient(circle at 24% 48%, rgba(255,255,255,.34) 0 1px, transparent 1.8px),
      radial-gradient(circle at 68% 56%, rgba(255,215,0,.40) 0 1px, transparent 1.8px),
      radial-gradient(circle at 44% 84%, rgba(255,255,255,.32) 0 1px, transparent 1.8px);
    opacity: .50;
  }

  #profileBgHost::after {
    content: "";
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: -1;
    background:
      radial-gradient(circle at 50% 14%, rgba(218,165,32,.12), transparent 34%),
      radial-gradient(circle at 50% 82%, rgba(75,0,130,.28), transparent 52%);
    filter: blur(10px) saturate(1.05);
  }

  /* Violet pulse (same as Welcome/Login) */
  @keyframes violetPulse {
    0% { filter: saturate(1) brightness(1); }
    50% { filter: saturate(1.18) brightness(1.04); }
    100% { filter: saturate(1) brightness(1); }
  }

  
`}</style>
      <div style={styles.topBrand} aria-hidden="true">
        <span style={styles.brandMark}>Z</span>
        <span style={styles.brandName}>Zodiacus</span>
      </div>
      <div style={styles.wheel} aria-hidden="true" />

      <button onClick={handleGoBack} style={styles.backButton} className="pf-back" aria-label={t('common:actions.back')}>
        <span style={styles.backIcon} aria-hidden="true">‹</span>
        <span style={styles.backText}>{t('common:actions.back')}</span>
      </button>
      {canSeeAdminPanel && (
        <button
          type="button"
          onClick={() => navigate('/admin/feed/reports')}
          style={styles.adminPanelButton}
          className={`pf-admin-panel${hasFreshPendingPulse ? ' is-alert' : ''}`}
          aria-label={reportCounts.pending > 0
            ? t('editor.admin.openAriaPending', { count: reportCounts.pending })
            : t('editor.admin.openAria')}
          title={reportCounts.pending > 0
            ? t('editor.admin.pendingTitle', { count: reportCounts.pending })
            : t('editor.admin.title')}
        >
          <span aria-hidden="true">🛡</span>
          <span>{t('editor.admin.panel')}</span>
          {reportCounts.pending > 0 && (
            <span style={styles.adminPanelBadge} aria-label={t('editor.admin.pendingAria', { count: reportCounts.pending })}>
              {reportCounts.pending > 99 ? '99+' : reportCounts.pending}
            </span>
          )}
        </button>
      )}

      <div style={styles.card} className="pf-profile-card">
        <div style={styles.cardHeader} className="pf-profile-header">
          <span style={styles.kicker} className="pf-profile-kicker">✦ {t('editor.header.kicker')}</span>
          <h2 style={styles.title} className="pf-profile-title">{t('editor.header.title')}</h2>
          <p style={styles.subtitle} className="pf-profile-subtitle">{t('editor.header.subtitle')}</p>
        </div>

        {/* Avatar preview destacado */}
        <div style={styles.avatarWrap} className="pf-avatar-wrap">
          <div aria-hidden="true" className="pf-halo" />
          <img
            src={previewPhoto || '/logo/Zodiacus.jpeg'}
            alt={t('editor.fields.avatarAlt')}
            crossOrigin="anonymous"
            loading="lazy"
            style={styles.avatar}
            className="pf-avatar-img"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = '/logo/Zodiacus.jpeg';
            }}
          />
        </div>

        <form onSubmit={handleSubmit} style={styles.form} encType="multipart/form-data" autoComplete="on" aria-busy={loading}>
          {/* Nombre SOLO lectura visual */}
          <label htmlFor="profileFullName" style={styles.label} className="pf-form-label">{t('editor.fields.username')}</label>
          <input
            id="profileFullName"
            name="fullName"
            type="text"
            value={fullName}
            readOnly
            style={{ ...styles.input, background: 'linear-gradient(180deg, rgba(255,255,255,0.14), rgba(255,255,255,0.08))', color: 'rgba(255,243,196,0.78)', cursor: 'not-allowed' }}
            className="pf-input"
          />

          {/* Bio */}
          <label htmlFor="profileBio" style={styles.label} className="pf-form-label">
  {t('editor.fields.bio')}{' '}
  <span style={{ fontWeight: 700, fontSize: 12, color: '#DAA520', textShadow: '0 1px 1px rgba(0,0,0,0.35), 0 0 6px rgba(218,165,32,0.30)' }}>
    {t('editor.fields.bioLimit', { count: BIO_MAX })}
  </span>
</label>
          <textarea
            id="profileBio"
            name="bio"
            value={bio}
            onChange={(e) => {
              const v = e.target.value || '';
              setBio(v.length > BIO_MAX ? v.slice(0, BIO_MAX) : v);
            }}
            style={styles.textarea}
            placeholder={t('editor.fields.bioPlaceholder', { count: BIO_MAX })}
            rows={4}
            maxLength={BIO_MAX}
            aria-describedby="bioCounter"
            className="pf-input"
          />
          <div id="bioCounter" style={{ marginTop: 4, fontSize: 12, color: bio.length >= BIO_MAX ? 'crimson' : '#6b5a8e', textAlign: 'right' }}>
            {bio.length}/{BIO_MAX}
          </div>
          {/* Emoji picker trigger */}
          <div style={styles.emojiPickerBar}>
            <button
              type="button"
              ref={emojiTriggerRef}
              onClick={() => setShowEmojiPicker(v => !v)}
              style={styles.emojiTriggerBtn}
              className="pf-emoji-trigger"
              disabled={!EmojiPicker}
              aria-label={EmojiPicker ? t('editor.emoji.insert') : t('editor.emoji.enableHint')}
              title={EmojiPicker ? t('editor.emoji.insert') : t('editor.emoji.missingTitle')}
            >
              <span aria-hidden>😀</span>
            </button>
            {!EmojiPicker && (
              <small style={{marginLeft: 8, color: '#6b5a8e'}}>
                {t('editor.emoji.missing')}
              </small>
            )}
          </div>
          {EmojiPicker && showEmojiPicker && (
            <div ref={emojiWrapRef} style={styles.emojiPickerWrap}>
              <EmojiPicker
                lazyLoadEmojis
                searchDisabled
                skinTonesDisabled
                previewConfig={{ showPreview: false }}
                height={260}
                width={320}
                onEmojiClick={(emojiData) => {
                  const e = emojiData?.emoji || '';
                  setBio(b => appendIfFits(b, e));
                  setShowEmojiPicker(false);
                }}
              />
            </div>
          )}

          {/* Preview toggle only */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 6 }}>
            <label style={{ fontSize: 13, color: '#DAA520', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                id="profilePreviewToggle"
                name="showPreview"
                type="checkbox"
                checked={showPreview}
                onChange={(e) => setShowPreview(e.target.checked)}
              />
              {t('editor.fields.preview')}
            </label>
          </div>

{/* Live Preview card */}
{showPreview && (
  <div style={styles.bioPreview} className="pf-bio-preview">
    <div style={styles.bioPreviewHeader}>
      <img src={previewPhoto || '/logo/Zodiacus.jpeg'} alt="" aria-hidden="true" style={styles.bioPreviewAvatar} />
      <div>
        <div style={styles.bioPreviewName}>{fullName || t('user.fallbackName')}</div>
        <div style={styles.bioPreviewMeta}>
          {user?.zodiacSign ? `${getSignEmoji(user.zodiacSign)} ${zodiacDisplayName}` : '—'}
          {gender ? ` • ${genderEmoji(gender)} ${t(GENDER_OPTIONS.find((option) => option.value === gender)?.labelKey || 'editor.gender.other').replace(/\s[♂♀⚧]$/, '')}` : ''}
        </div>
      </div>
    </div>
    <div style={styles.bioPreviewBody}>{bio || t('editor.fields.bioPreviewEmpty')}</div>
  </div>
)}


          {/* Género */}
          <label htmlFor="profileGender" style={styles.label} className="pf-form-label">
            {t('editor.fields.gender')} {gender && <span style={{ marginLeft: 8 }}>{genderEmoji(gender)}</span>}
          </label>
          <select
            id="profileGender"
            name="gender"
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            style={{ ...styles.input, appearance: 'auto' }}
            className="pf-input"
          >
            {GENDER_OPTIONS.map(opt => (
              <option key={opt.value || 'none'} value={opt.value}>{t(opt.labelKey)}</option>
            ))}
          </select>

          <small style={{ color: '#DAA520', marginTop: 6, display: 'block', fontWeight: 800, textShadow: '0 1px 2px rgba(0,0,0,0.35), 0 0 8px rgba(218,165,32,0.35)' }}>
            {user?.gender == null
              ? t('editor.gender.setNow')
              : t('editor.gender.changesLeft', { count: genderChangeLeft ?? 1 })}
          </small>

          {/* Foto */}
          <label htmlFor="profilePhoto" style={styles.label} className="pf-form-label">{t('editor.fields.photo')}</label>
          <input
            id="profilePhoto"
            name="photo"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handlePhotoChange}
            style={styles.visuallyHiddenFileInput}
          />
          <div style={styles.filePicker} className="pf-file-picker">
            <label
              htmlFor="profilePhoto"
              style={styles.filePickerButton}
              className="pf-file-picker-button"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' && e.key !== ' ') return;
                e.preventDefault();
                e.currentTarget.control?.click();
              }}
            >
              {t('editor.filePicker.chooseImage')}
            </label>
            <span
              style={styles.filePickerStatus}
              className="pf-file-picker-status"
              aria-live="polite"
              aria-label={photo ? t('editor.filePicker.selectedFileAria', { name: photo.name }) : undefined}
            >
              {photo?.name || t('editor.filePicker.noFileSelected')}
            </span>
          </div>

          {/* Leyenda de privacidad (solo foto, sin DNI) */}
          <div style={{ marginTop: 10, fontSize: 12, color: 'rgba(255,255,255,0.72)', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,215,0,0.12)', padding: 9, borderRadius: 14 }}>
            🔒 {t('editor.privacy.photo')}
          </div>

          <div style={styles.verificationCard} className="pf-verification-card">
            <div style={styles.verificationHeader}>
              <div style={styles.verificationBadge}>🛡️</div>
              <div>
                <div style={styles.verificationTitle}>{t('editor.verification.title')}</div>
                <div style={styles.verificationText}>
                  {t('editor.verification.empty')}
                </div>
              </div>
            </div>

            <label htmlFor="profileDni" style={styles.label} className="pf-form-label">{t('editor.verification.title')}</label>
            <input
              id="profileDni"
              name="dni"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleDniChange}
              style={styles.visuallyHiddenFileInput}
            />
            <div style={styles.filePicker} className="pf-file-picker">
              <label
                htmlFor="profileDni"
                style={styles.filePickerButton}
                className="pf-file-picker-button"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' && e.key !== ' ') return;
                  e.preventDefault();
                  e.currentTarget.control?.click();
                }}
              >
                {t('editor.filePicker.chooseImage')}
              </label>
              <span
                style={styles.filePickerStatus}
                className="pf-file-picker-status"
                aria-live="polite"
                aria-label={dni ? t('editor.filePicker.selectedFileAria', { name: dni.name }) : undefined}
              >
                {dni?.name || t('editor.filePicker.noFileSelected')}
              </span>
            </div>

            <div style={styles.securityNote}>
              <span style={styles.securityIcon}>🛡️</span>
              <span>{t('editor.verification.security')}</span>
            </div>

            <div style={styles.privacyNote}>
              <div style={styles.privacyIcon} aria-hidden="true">🔒</div>
              <div>
                <div style={styles.privacyTitle}>{t('editor.verification.protectedTitle')}</div>
                <div style={styles.privacyText}>
                  {t('editor.verification.description')} {t('editor.verification.agreementPrefix')} <a href="/privacidad" style={styles.privacyLink}>{t('editor.privacy.policy')}</a> {t('editor.verification.and')} <a href="/terminos" style={styles.privacyLink}>{t('editor.privacy.terms')}</a>.
                </div>
              </div>
            </div>
          </div>

          <button
            type="submit"
            style={{ ...styles.button, opacity: loading || !isDirty ? 0.6 : 1 }}
            disabled={loading || !isDirty}
            className="pf-submit"
            aria-disabled={loading || !isDirty}
          >
            {loading ? t('editor.actions.updating') : (isDirty ? t('editor.actions.save') : t('editor.actions.noChanges'))}
          </button>

          <div
            id="profileFeedback"
            role="status"
            aria-live="polite"
            style={{ ...styles.feedback, visibility: message ? 'visible' : 'hidden', minHeight: 20, overflowAnchor: 'none', }}
          >
            {visibleMessage || ''}
          </div>
        </form>

        <section style={styles.dangerZone} aria-labelledby="notification-preferences-title">
          <h2 id="notification-preferences-title" style={styles.dangerTitle}>Notification preferences</h2>
          <p style={styles.dangerText}>Choose which Android notifications you want to receive.</p>
          <button type="button" style={styles.button} onClick={() => navigate('/notification-preferences')}>
            Manage notifications
          </button>
        </section>

        <section style={styles.dangerZone} aria-labelledby="delete-account-title">
          <h2 id="delete-account-title" style={styles.dangerTitle}>{t('editor.deleteAccount.title')}</h2>
          <p style={styles.dangerText}>{t('editor.deleteAccount.description')}</p>
          <button type="button" style={styles.dangerButton} onClick={() => setDeleteOpen(true)}>
            {t('editor.deleteAccount.open')}
          </button>
        </section>

        {deleteOpen && (
          <div role="presentation" style={styles.modalBackdrop} onMouseDown={(event) => {
            if (event.target === event.currentTarget && !deleteBusy) setDeleteOpen(false);
          }}>
            <div role="dialog" aria-modal="true" aria-labelledby="delete-confirm-title" style={styles.deleteDialog}>
              <h2 id="delete-confirm-title" style={styles.dangerTitle}>{t('editor.deleteAccount.confirmTitle')}</h2>
              <p style={styles.dangerText}>{t('editor.deleteAccount.confirmDescription', { phrase: t('editor.deleteAccount.confirmationPhrase') })}</p>
              <label htmlFor="deleteAccountConfirmation" style={styles.label}>{t('editor.deleteAccount.confirmLabel')}</label>
              <input
                id="deleteAccountConfirmation"
                value={deleteConfirmation}
                onChange={(event) => setDeleteConfirmation(event.target.value)}
                disabled={deleteBusy}
                autoComplete="off"
                style={styles.input}
              />
              {deleteError ? <p role="alert" style={styles.deleteError}>{deleteError}</p> : null}
              <div style={styles.deleteActions}>
                <button type="button" disabled={deleteBusy} onClick={() => setDeleteOpen(false)} style={styles.cancelButton}>
                  {t('editor.deleteAccount.cancel')}
                </button>
                <button
                  type="button"
                  disabled={deleteBusy || deleteConfirmation !== t('editor.deleteAccount.confirmationPhrase')}
                  onClick={handleDeleteAccount}
                  style={{ ...styles.dangerButton, opacity: deleteBusy || deleteConfirmation !== t('editor.deleteAccount.confirmationPhrase') ? 0.55 : 1 }}
                >
                  {deleteBusy ? t('editor.deleteAccount.deleting') : t('editor.deleteAccount.confirm')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div aria-hidden="true" style={{ height: 'max(12vh, 72px)' }} />
      <style>
        {`
          @keyframes fadeInUp {
            0% { opacity: 0; transform: translateY(10px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          /* Back button premium interactions */
          .pf-back:hover {
            transform: translateY(-1px) scale(1.02);
            border-color: rgba(255,215,0,0.54);
            box-shadow: 0 14px 30px rgba(0,0,0,0.34), 0 0 18px rgba(218,165,32,0.18), inset 0 1px 0 rgba(255,255,255,0.12);
            filter: saturate(1.05) brightness(1.05);
          }
          .pf-back:active {
            transform: translateY(0) scale(0.995);
            box-shadow: 0 8px 20px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.10);
          }
          .pf-back:focus-visible {
            outline: none;
            box-shadow: 0 0 0 3px rgba(218,165,32,0.34), 0 10px 24px rgba(0,0,0,0.30);
          }
          .pf-admin-panel:hover {
            transform: translateY(-1px);
            box-shadow: 0 12px 28px rgba(0,0,0,0.24), 0 0 0 1px rgba(218,165,32,0.14) inset;
            filter: brightness(1.03);
          }
          .pf-admin-panel:active {
            transform: translateY(0);
          }
          .pf-admin-panel:focus-visible {
            outline: none;
            box-shadow: 0 0 0 3px rgba(218,165,32,0.22), 0 8px 22px rgba(0,0,0,0.22);
          }
          .pf-admin-panel.is-alert {
            animation: pfAdminPulse 1.15s ease-in-out 2;
          }
          @keyframes pfAdminPulse {
            0% { transform: scale(1); box-shadow: 0 8px 22px rgba(0,0,0,0.22); }
            50% { transform: scale(1.04); box-shadow: 0 10px 30px rgba(187, 42, 89, 0.34); }
            100% { transform: scale(1); box-shadow: 0 8px 22px rgba(0,0,0,0.22); }
          }
          /* Inputs & textarea focus ring */
          .pf-input:focus {
            outline: none !important;
            border-color: rgba(75,0,130,0.9) !important;
            box-shadow: 0 0 0 3px rgba(75,0,130,0.18), inset 0 1px 2px rgba(218,165,32,0.12) !important;
            background-color: rgba(255,248,225,0.95);
          }
          /* Submit button subtle hover */
          .pf-submit:hover {
            transform: translateY(-1px);
            box-shadow: 0 14px 30px rgba(218,165,32,0.38);
          }
          .pf-submit:active {
            transform: translateY(0);
            box-shadow: 0 8px 20px rgba(218,165,32,0.32);
          }
          .pf-submit:focus-visible {
            outline: none;
            box-shadow: 0 0 0 3px rgba(218,165,32,0.35), 0 12px 26px rgba(218,165,32,0.30);
          }
          /* Golden halo behind profile avatar */
          .pf-halo{
            position: absolute;
            inset: -10px;
            border-radius: 50%;
            background: radial-gradient(closest-side, rgba(218,165,32,0.55), rgba(218,165,32,0.25) 38%, rgba(218,165,32,0.10) 62%, rgba(218,165,32,0.0) 75%);
            filter: blur(6px);
            animation: pfHaloPulse 2.8s ease-in-out infinite;
            pointer-events: none;
            z-index: 0;
          }
          @keyframes pfHaloPulse{
            0%   { transform: scale(0.985); opacity: 0.85; }
            50%  { transform: scale(1.015); opacity: 1; }
            100% { transform: scale(0.985); opacity: 0.85; }
          }
          @keyframes wheel-spin {
            from { transform: translate(-50%, -50%) rotate(0deg) scale(1); }
            to   { transform: translate(-50%, -50%) rotate(360deg) scale(1); }
          }

          @media (max-width: 520px) {
            #profileBgHost {
              padding-top: calc(env(safe-area-inset-top, 0px) + 76px) !important;
              padding-left: 10px !important;
              padding-right: 10px !important;
              padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 22px) !important;
            }
            .pf-back,
            .pf-admin-panel {
              min-height: 36px !important;
              padding: 7px 11px !important;
              border-radius: 18px !important;
              font-size: 0.84rem !important;
            }
            .pf-admin-panel {
              top: calc(env(safe-area-inset-top, 0px) + 18px) !important;
            }
            .pf-back {
              top: calc(env(safe-area-inset-top, 0px) + 18px) !important;
            }
            .pf-input {
              font-size: 15px !important;
            }
            .pf-profile-card {
              border-radius: 24px !important;
              padding: 15px 14px 14px !important;
            }
            .pf-profile-header {
              margin-bottom: 10px !important;
            }
            .pf-profile-kicker {
              padding: 4px 8px !important;
              margin-bottom: 6px !important;
              font-size: 0.62rem !important;
              letter-spacing: .10em !important;
            }
            .pf-profile-title {
              font-size: 1.88rem !important;
              line-height: 0.96 !important;
            }
            .pf-profile-subtitle {
              display: none !important;
            }
            .pf-avatar-wrap {
              margin: 2px 0 10px !important;
            }
            .pf-avatar-img {
              width: 88px !important;
              height: 88px !important;
            }
            .pf-form-label {
              margin-top: 10px !important;
              margin-bottom: 4px !important;
              font-size: 0.78rem !important;
            }
            .pf-input {
              min-height: 42px !important;
              padding: 10px 12px !important;
              border-radius: 14px !important;
            }
            textarea.pf-input {
              min-height: 70px !important;
            }
            .pf-bio-preview {
              margin-top: 9px !important;
              padding: 10px 11px !important;
              border-radius: 17px !important;
            }
            .pf-verification-card {
              margin-top: 12px !important;
              padding: 12px 11px !important;
              border-radius: 18px !important;
            }
            .pf-submit {
              margin-top: 16px !important;
              padding: 11px !important;
              border-radius: 14px !important;
              font-size: 1rem !important;
            }
          }

          /* Reduced motion preference */
          @media (prefers-reduced-motion: reduce) {
            .pf-halo { animation: none !important; }
            .pf-submit:hover, .pf-back:hover { transform: none !important; box-shadow: none !important; }
            .pf-submit:active, .pf-back:active { transform: none !important; }
            .pf-submit:focus-visible, .pf-back:focus-visible { box-shadow: 0 0 0 3px rgba(218,165,32,0.35); }
          }

          /* Aesthetic refinements */
          .pf-input:hover {
  border-color: rgba(75,0,130,0.75);
  background-color: rgba(255,255,255,0.98);
}
          .pf-submit[disabled] {
            cursor: not-allowed;
            filter: grayscale(0.2) opacity(0.9);
          }
          .pf-emoji-trigger:hover {
            filter: brightness(1.03) saturate(1.02);
            transform: translateY(-0.5px);
          }
          .pf-file-picker-button:hover {
            filter: brightness(1.06) saturate(1.04);
            transform: translateY(-1px);
          }
          .pf-file-picker-button:focus-visible {
            outline: none;
            box-shadow: 0 0 0 3px rgba(218,165,32,0.28), 0 10px 22px rgba(0,0,0,0.22);
          }
          /* Compact emoji-picker-react overrides */
          .epr-main {
            --epr-emoji-size: 20px;
            --epr-emoji-gap: 6px;
            --epr-picker-border-color: rgba(75,0,130,0.25);
          }

        `}
      </style>
    </div>
  );
};

const styles = {
  dangerZone: {
    marginTop: 24,
    padding: 16,
    border: '1px solid rgba(255,90,90,0.5)',
    borderRadius: 16,
    background: 'rgba(90,0,20,0.2)',
  },
  dangerTitle: { margin: '0 0 8px', color: '#FFD6D6', fontSize: 18 },
  dangerText: { color: 'rgba(255,255,255,0.78)', lineHeight: 1.5 },
  dangerButton: {
    padding: '11px 14px', borderRadius: 12, border: '1px solid #ff7777',
    background: '#9d1636', color: '#fff', fontWeight: 800, cursor: 'pointer',
  },
  modalBackdrop: {
    position: 'fixed', inset: 0, zIndex: 10000, display: 'grid', placeItems: 'center',
    padding: 18, background: 'rgba(0,0,0,0.78)',
  },
  deleteDialog: {
    width: 'min(460px, 100%)', padding: 20, borderRadius: 18,
    background: '#21122d', border: '1px solid rgba(255,110,110,0.55)',
    boxShadow: '0 24px 70px rgba(0,0,0,0.6)',
  },
  deleteActions: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 },
  cancelButton: {
    padding: '11px 14px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.25)',
    background: 'transparent', color: '#fff', fontWeight: 700, cursor: 'pointer',
  },
  deleteError: { color: '#ffaaaa', fontWeight: 700 },
  topBrand: {
    position: 'fixed',
    top: 'calc(env(safe-area-inset-top, 0px) + 10px)',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 6,
    pointerEvents: 'none',
    display: 'grid',
    justifyItems: 'center',
    gap: 3,
    opacity: 0.98,
  },
  brandMark: {
    width: 30,
    height: 30,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    border: '1px solid rgba(255,215,0,0.62)',
    color: '#FFD95A',
    fontWeight: 950,
    fontSize: 14,
    background: 'radial-gradient(circle at 50% 28%, rgba(255,215,0,0.18), rgba(75,0,130,0.26))',
    boxShadow: '0 0 18px rgba(218,165,32,0.20)',
    fontFamily: 'Georgia, serif',
  },
  brandName: {
    color: '#F3C96B',
    fontWeight: 950,
    fontSize: 16,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    textShadow: '0 2px 14px rgba(218,165,32,0.30)',
    fontFamily: 'Georgia, serif',
    lineHeight: 1,
  },
emojiPickerBar: { display: 'flex', alignItems: 'center', marginTop: 8 },
emojiTriggerBtn: {
  width: 40,
  height: 40,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 12,
  border: '1px solid rgba(75,0,130,0.35)',
  background: 'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(245,235,255,0.9))',
  cursor: 'pointer',
  fontSize: 18,
  color: '#4B0082',
  boxShadow: '0 2px 6px rgba(0,0,0,0.08)'
},
emojiPickerWrap: {
  marginTop: 8,
  borderRadius: 12,
  border: '1px solid rgba(75,0,130,0.25)',
  background: 'rgba(255,255,255,0.98)',
  boxShadow: '0 14px 36px rgba(0,0,0,0.12)',
  overflow: 'hidden',
  maxWidth: 340,
  overflowAnchor: 'none',
},
  bioPreview: {
    marginTop: 12,
    padding: '12px 13px',
    borderRadius: 20,
    background: 'linear-gradient(180deg, rgba(255,255,255,0.11), rgba(255,255,255,0.06))',
    border: '1px solid rgba(255,215,0,0.18)',
    boxShadow: '0 12px 26px rgba(0,0,0,0.20)',
  },
  bioPreviewHeader: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 },
  bioPreviewAvatar: { width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', border: '2px solid #DAA520' },
  bioPreviewName: { fontWeight: 900, color: '#fff', textShadow: '0 1px 10px rgba(218,165,32,0.22)' },
  bioPreviewMeta: { fontSize: 12, color: 'rgba(255,255,255,0.62)' },
  bioPreviewBody: { marginTop: 6, fontSize: 15.5, color: '#FFF3C4', textShadow: 'none' },

  container: {
    minHeight: '100dvh',
    overflowX: 'hidden',
    overflowY: 'visible',
    overscrollBehaviorY: 'auto',
    overflowAnchor: 'none',
    touchAction: 'pan-y',
    padding: 'calc(env(safe-area-inset-top, 0px) + 84px) 12px calc(env(safe-area-inset-bottom, 0px) + 28px)',
    color: '#fff',
    position: 'relative',
    isolation: 'isolate',
    display: 'block',
  },
  wheel: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    width: 'min(96svmin, 980px)',
    height: 'min(96svmin, 980px)',
    transform: 'translate(-50%, -50%)',
    backgroundImage: 'url("/logo/Zodiacus.jpeg")',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    filter: 'brightness(1.08) contrast(1.12) saturate(1.08) drop-shadow(0 0 42px rgba(154,47,255,0.22))',
    borderRadius: '50%',
    opacity: 0.18,
    animation: 'wheel-spin 160s linear infinite',
    transformOrigin: 'center center',
    pointerEvents: 'none',
    zIndex: -2,
  },
  backButton: {
    position: 'fixed',
    top: 'calc(env(safe-area-inset-top, 0px) + 20px)',
    left: 'calc(env(safe-area-inset-left, 0px) + 12px)',
    padding: '8px 12px 8px 10px',
    minHeight: 38,
    minWidth: 38,
    borderRadius: 999,
    border: '1px solid rgba(255,215,0,0.34)',
    background: 'linear-gradient(135deg, rgba(21,5,45,0.82), rgba(75,0,130,0.72))',
    color: '#FFE49A',
    fontWeight: 950,
    cursor: 'pointer',
    boxShadow: '0 10px 24px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.10)',
    textShadow: '0 1px 8px rgba(0,0,0,0.45)',
    transition: 'transform .12s ease, box-shadow .12s ease, filter .12s ease, border-color .12s ease',
    zIndex: 8,
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
  },
  backIcon: {
    width: 22,
    height: 22,
    borderRadius: '50%',
    display: 'inline-grid',
    placeItems: 'center',
    background: 'linear-gradient(135deg, rgba(255,215,0,0.96), rgba(218,165,32,0.82))',
    color: '#3B006F',
    fontSize: 24,
    fontWeight: 950,
    lineHeight: '18px',
    boxShadow: '0 0 14px rgba(218,165,32,0.24)',
    transform: 'translateY(-1px)',
  },
  backText: {
    display: 'inline-block',
    color: '#FFE49A',
    fontWeight: 950,
    fontSize: 13,
    letterSpacing: 0.2,
  },
  adminPanelButton: {
    position: 'fixed',
    top: 'calc(env(safe-area-inset-top, 0px) + 22px)',
    right: 'calc(env(safe-area-inset-right, 0px) + 12px)',
    padding: '9px 13px',
    minHeight: 40,
    borderRadius: 20,
    border: '1px solid rgba(255,215,0,0.28)',
    background: 'linear-gradient(135deg, rgba(35,14,58,0.90), rgba(82,25,126,0.86))',
    color: '#FFE49A',
    fontWeight: 950,
    fontSize: 13,
    letterSpacing: 0.2,
    cursor: 'pointer',
    boxShadow: '0 10px 24px rgba(0,0,0,0.26)',
    zIndex: 8,
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  adminPanelBadge: {
    minWidth: 20,
    height: 20,
    padding: '0 6px',
    borderRadius: 999,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(180deg, #FF5C8A, #D61F5C)',
    color: '#fff',
    fontWeight: 900,
    fontSize: 11,
    lineHeight: 1,
    boxShadow: '0 6px 14px rgba(214,31,92,0.34)',
    border: '1px solid rgba(255,255,255,0.18)',
  },
  card: {
    margin: '0 auto',
    width: '100%',
    maxWidth: 560,
    background: 'linear-gradient(180deg, rgba(20,11,38,0.84), rgba(10,7,20,0.94))',
    color: '#fff',
    borderRadius: 28,
    padding: '18px 16px 16px',
    boxShadow: '0 22px 58px rgba(0,0,0,0.38), inset 0 1px 0 rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,215,0,0.20)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    animation: 'fadeInUp 420ms ease-out',
    zIndex: 0,
    position: 'relative',
    overflow: 'hidden',
  },
  cardHeader: {
    textAlign: 'center',
    marginBottom: 14,
    position: 'relative',
    zIndex: 1,
  },
  kicker: {
    display: 'inline-flex',
    padding: '6px 10px',
    borderRadius: 999,
    border: '1px solid rgba(255,215,0,0.20)',
    background: 'rgba(255,255,255,0.07)',
    color: '#F3C96B',
    fontSize: 11,
    fontWeight: 950,
    letterSpacing: '.14em',
    textTransform: 'uppercase',
    marginBottom: 10,
  },
  subtitle: {
    margin: '8px auto 0',
    maxWidth: 420,
    color: 'rgba(255,255,255,0.68)',
    fontSize: 12.5,
    lineHeight: 1.36,
    fontWeight: 650,
  },
  title: {
    textAlign: 'center',
    color: '#fff',
    margin: 0,
    fontWeight: 950,
    fontSize: 'clamp(30px, 9vw, 38px)',
    letterSpacing: -0.6,
    textShadow: '0 2px 16px rgba(218,165,32,0.22)',
  },
  avatarWrap: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '6px 0 16px',
  },
  avatar: {
    width: 96,
    height: 96,
    borderRadius: '50%',
    objectFit: 'cover',
    border: '3px solid rgba(255,215,0,0.86)',
    boxShadow: '0 10px 22px rgba(0,0,0,0.25), 0 0 18px rgba(218,165,32,0.35), 0 0 34px rgba(218,165,32,0.20)',
    backgroundColor: '#160027',
    position: 'relative',
    zIndex: 1,
  },
  form: { display: 'flex', flexDirection: 'column' },
  label: {
    marginBottom: 6,
    marginTop: 14,
    fontWeight: 900,
    fontSize: 13,
    color: '#F3C96B',
    letterSpacing: 0.2,
    textShadow: '0 1px 8px rgba(0,0,0,0.30)',
  },
  input: {
    marginTop: 6,
    padding: '13px 14px',
    borderRadius: 16,
    border: '1px solid rgba(255,215,0,0.20)',
    fontSize: 16,
    outline: 'none',
    transition: 'border-color 180ms ease, box-shadow 180ms ease, background-color 180ms ease',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.09), rgba(255,255,255,0.055))',
    color: '#FFF3C4',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
  },
  textarea: {
    marginTop: 6,
    padding: '13px 14px',
    borderRadius: 16,
    border: '1px solid rgba(255,215,0,0.20)',
    fontSize: 16,
    resize: 'vertical',
    minHeight: 78,
    background: 'linear-gradient(180deg, rgba(255,255,255,0.09), rgba(255,255,255,0.055))',
    color: '#FFF3C4',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
    outline: 'none',
  },
  visuallyHiddenFileInput: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  filePicker: {
    width: '100%',
    minWidth: 0,
    marginTop: 6,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: 8,
    borderRadius: 16,
    border: '1px solid rgba(255,215,0,0.20)',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.09), rgba(255,255,255,0.055))',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
    boxSizing: 'border-box',
  },
  filePickerButton: {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 38,
    padding: '8px 12px',
    borderRadius: 12,
    border: '1px solid rgba(218,165,32,0.42)',
    background: 'linear-gradient(135deg, rgba(138,43,226,0.86), rgba(218,165,32,0.78))',
    color: '#fff',
    fontSize: 13,
    fontWeight: 900,
    cursor: 'pointer',
    boxShadow: '0 8px 18px rgba(0,0,0,0.20)',
    transition: 'transform 120ms ease, filter 160ms ease, box-shadow 160ms ease',
    textAlign: 'center',
  },
  filePickerStatus: {
    minWidth: 0,
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: 'rgba(255,243,196,0.78)',
    fontSize: 13,
  },
  button: {
    width: '100%',
    marginTop: 18,
    padding: '13px',
    borderRadius: 16,
    background: 'linear-gradient(135deg, #8A2BE2, #DAA520)',
    color: '#fff',
    fontWeight: 950,
    fontSize: 17,
    cursor: 'pointer',
    border: '1px solid rgba(255,215,0,0.26)',
    boxShadow: '0 16px 30px rgba(0,0,0,0.26), 0 0 22px rgba(218,165,32,0.18)',
    transition: 'transform 120ms ease, box-shadow 180ms ease, filter 180ms ease',
  },
  verificationCard: {
    marginTop: 14,
    background: 'linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.04))',
    border: '1px solid rgba(255,215,0,0.16)',
    borderRadius: 22,
    padding: '15px 14px 13px',
    boxShadow: '0 14px 30px rgba(0,0,0,0.20)',
  },
  verificationHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    marginBottom: 8,
  },
  verificationBadge: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    background: 'rgba(218,165,32,0.18)',
    border: '1px solid rgba(75,0,130,0.18)',
    flexShrink: 0,
    fontSize: 16,
  },
  verificationTitle: {
    fontWeight: 900,
    fontSize: 15,
    color: '#fff',
    marginBottom: 4,
  },
  verificationText: {
    fontSize: 13,
    lineHeight: 1.45,
    color: 'rgba(255,255,255,0.62)',
  },
  securityNote: {
    marginTop: 10,
    marginBottom: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'rgba(72, 137, 255, 0.10)',
    border: '1px solid rgba(104, 164, 255, 0.24)',
    padding: '9px 12px',
    borderRadius: 14,
    color: '#DCEAFF',
    fontSize: 13,
    fontWeight: 700,
  },
  securityIcon: {
    fontSize: 16,
  },
  privacyNote: {
    marginTop: 10,
    marginBottom: 6,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    backgroundColor: 'rgba(255,215,0,0.07)',
    border: '1px solid rgba(255,215,0,0.16)',
    padding: '11px 12px',
    borderRadius: 16,
    color: '#FFF3C4',
    boxShadow: '0 8px 18px rgba(0,0,0,0.14)',
  },
  privacyIcon: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    background: 'rgba(218,165,32,0.18)',
    border: '2px solid #DAA520',
    color: '#4B0082',
    fontSize: 16,
    flexShrink: 0,
    marginTop: 2,
    boxShadow: '0 4px 10px rgba(218,165,32,0.18)',
  },
  privacyTitle: {
    fontWeight: 900,
    fontSize: 14,
    color: '#fff',
    marginBottom: 4,
  },
  privacyText: {
    fontSize: 12,
    lineHeight: 1.45,
    color: 'rgba(255,255,255,0.66)',
  },
  privacyLink: {
    color: '#4B0082',
    fontWeight: 700,
    textDecoration: 'underline',
  },
  feedback: {
    marginTop: 14,
    textAlign: 'center',
    fontWeight: 800,
    color: '#F3C96B',
    textShadow: '0 1px 10px rgba(0,0,0,0.35)',
  },
};

export default ProfileScreen;
